
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { toast } from "sonner";
import { z } from "zod";

const heroContentSchema = z.object({
  supertitle: z.string().optional(),
  title_line_1: z.string().min(1, "Title line 1 is required"),
  title_line_2: z.string().optional(),
  title_highlight: z.string().optional(),
  subtitle: z.string().optional(),
  cta1_text: z.string().min(1, "Primary CTA text is required"),
  cta1_path: z.string().min(1, "Primary CTA path is required"),
  cta2_text: z.string().optional(),
  cta2_path: z.string().optional(),
  is_active: z.boolean().default(true),
  sort_order: z.number().min(0).default(0)
});

type HeroContentFormData = z.infer<typeof heroContentSchema>;

// Define the type that Supabase expects for inserts
type HeroContentInsertData = {
  supertitle?: string;
  title_line_1: string;
  title_line_2?: string;
  title_highlight?: string;
  subtitle?: string;
  cta1_text: string;
  cta1_path: string;
  cta2_text?: string;
  cta2_path?: string;
  is_active: boolean;
  sort_order: number;
};

interface HeroContentFormProps {
  content?: any;
  onClose: () => void;
}

const HeroContentForm = ({ content, onClose }: HeroContentFormProps) => {
  const queryClient = useQueryClient();
  const isEditing = !!content;

  const form = useForm<HeroContentFormData>({
    resolver: zodResolver(heroContentSchema),
    defaultValues: {
      supertitle: "",
      title_line_1: "",
      title_line_2: "",
      title_highlight: "",
      subtitle: "",
      cta1_text: "",
      cta1_path: "",
      cta2_text: "",
      cta2_path: "",
      is_active: true,
      sort_order: 0
    }
  });

  useEffect(() => {
    if (content) {
      form.reset({
        supertitle: content.supertitle || "",
        title_line_1: content.title_line_1 || "",
        title_line_2: content.title_line_2 || "",
        title_highlight: content.title_highlight || "",
        subtitle: content.subtitle || "",
        cta1_text: content.cta1_text || "",
        cta1_path: content.cta1_path || "",
        cta2_text: content.cta2_text || "",
        cta2_path: content.cta2_path || "",
        is_active: content.is_active,
        sort_order: content.sort_order
      });
    }
  }, [content, form]);

  const mutation = useMutation({
    mutationFn: async (data: HeroContentFormData) => {
      if (isEditing) {
        const { error } = await supabase
          .from('hero_content')
          .update(data)
          .eq('id', content.id);
        if (error) throw error;
      } else {
        // Type assertion since Zod validation ensures all required fields are present
        const { error } = await supabase
          .from('hero_content')
          .insert(data as HeroContentInsertData);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hero-content'] });
      toast.success(isEditing ? 'Content updated' : 'Content created');
      onClose();
    },
    onError: (error) => {
      toast.error(isEditing ? 'Failed to update content' : 'Failed to create content');
      // Error handled by form validation
    }
  });

  const onSubmit = (data: HeroContentFormData) => {
    mutation.mutate(data);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Hero Content' : 'Create Hero Content'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="supertitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Supertitle</FormLabel>
                  <FormControl>
                    <Input placeholder="Timeless Elegance, Modern Design" {...field} />
                  </FormControl>
                  <FormDescription>Optional small text above the main title</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="title_line_1"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title Line 1 *</FormLabel>
                    <FormControl>
                      <Input placeholder="Where Luxury" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title_line_2"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title Line 2</FormLabel>
                    <FormControl>
                      <Input placeholder="Meets" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="title_highlight"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Highlight Text</FormLabel>
                    <FormControl>
                      <Input placeholder="Legacy" {...field} />
                    </FormControl>
                    <FormDescription>This text will be highlighted in gold</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="subtitle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subtitle</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Discover curated collections of unparalleled craftsmanship..."
                      className="min-h-[80px]"
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cta1_text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary CTA Text *</FormLabel>
                    <FormControl>
                      <Input placeholder="Explore Collections" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cta1_path"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Primary CTA Path *</FormLabel>
                    <FormControl>
                      <Input placeholder="/products" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="cta2_text"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Secondary CTA Text</FormLabel>
                    <FormControl>
                      <Input placeholder="Our Philosophy" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cta2_path"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Secondary CTA Path</FormLabel>
                    <FormControl>
                      <Input placeholder="/about" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="is_active"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel>Active</FormLabel>
                      <FormDescription>Show this content in the hero section</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="sort_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Sort Order</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min="0" 
                        {...field}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormDescription>Lower numbers appear first</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" loading={mutation.isPending}>
                {isEditing ? 'Update' : 'Create'} Content
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default HeroContentForm;
